import type { Path, Waypoint } from '../../Components/PathBuilder/pathBuilderTypes';
import { PATH_BUILDER_SPEED_CONSTRAINTS } from '../constants';

export const DEFAULT_PATH_SEGMENT_SPEED = 1;

export function clampSegmentSpeed(value: number): number {
  return Math.min(PATH_BUILDER_SPEED_CONSTRAINTS.max, Math.max(PATH_BUILDER_SPEED_CONSTRAINTS.min, value));
}

type WaypointJson = Waypoint & { speed?: number };

function stripWaypointSpeed(waypoints: WaypointJson[]): Waypoint[] {
  return waypoints.map(({ speed: _omit, ...rest }) => rest as Waypoint);
}

/** Normalize a path from disk or legacy shape: strip per-waypoint speed, ensure segmentSpeeds length and clamped values. */
export function migratePath(path: Path & { waypoints?: WaypointJson[] }): Path {
  const raw = path.waypoints ?? [];
  const waypoints = stripWaypointSpeed(raw);
  const n = Math.max(0, waypoints.length - 1);
  let segmentSpeeds = [...(path.segmentSpeeds ?? [])];

  if (segmentSpeeds.length !== n) {
    segmentSpeeds = [];
    for (let i = 0; i < n; i++) {
      const fromSeg = path.segmentSpeeds?.[i];
      const fromWp = raw[i]?.speed;
      const val =
        typeof fromSeg === 'number' && !Number.isNaN(fromSeg)
          ? fromSeg
          : typeof fromWp === 'number' && !Number.isNaN(fromWp)
          ? fromWp
          : DEFAULT_PATH_SEGMENT_SPEED;
      segmentSpeeds.push(clampSegmentSpeed(val));
    }
  } else {
    segmentSpeeds = segmentSpeeds.map((s) => clampSegmentSpeed(typeof s === 'number' && !Number.isNaN(s) ? s : DEFAULT_PATH_SEGMENT_SPEED));
  }

  return { ...path, waypoints, segmentSpeeds };
}

export function migrateProjectPaths<T extends { paths?: Path[] }>(project: T): T {
  const withPaths = !project.paths?.length
    ? project
    : { ...project, paths: project.paths.map((p) => migratePath(p as Path & { waypoints?: WaypointJson[] })) };
  const pr = project as { pathBuilderFieldRotation?: number };
  return {
    ...withPaths,
    pathBuilderFieldRotation: pr.pathBuilderFieldRotation ?? 0,
  } as T;
}

/** Trim/pad segment speeds to waypointCount - 1 (append uses default). */
export function syncSegmentSpeedsLen(prev: number[] | undefined, waypointCount: number): number[] {
  const n = Math.max(0, waypointCount - 1);
  const out = [...(prev ?? [])];
  if (out.length > n) out.length = n;
  while (out.length < n) out.push(DEFAULT_PATH_SEGMENT_SPEED);
  return out.map(clampSegmentSpeed);
}

/**
 * Keep segmentSpeeds aligned when waypoints are edited (insert/remove one waypoint).
 * Falls back to default-filled array if counts change unexpectedly.
 */
export function syncSegmentSpeedsAfterWaypointEdit(prev: number[] | undefined, oldWps: Waypoint[], newWps: Waypoint[]): number[] {
  const oldN = oldWps.length;
  const newN = newWps.length;
  const oldIds = oldWps.map((w) => w.id);
  const newIds = newWps.map((w) => w.id);

  if (newN < 2) return [];

  if (newN === oldN) {
    return syncSegmentSpeedsLen(prev, newN);
  }

  const prevSeg = prev ?? [];

  if (newN === oldN + 1) {
    let insertAt = newN - 1;
    for (let j = 0; j < newN; j++) {
      if (!oldIds.includes(newIds[j])) {
        insertAt = j;
        break;
      }
    }
    if (insertAt === newN - 1) {
      return [...syncSegmentSpeedsLen(prevSeg, oldN), DEFAULT_PATH_SEGMENT_SPEED].map(clampSegmentSpeed);
    }
    const splitSpeed = insertAt === 0 ? DEFAULT_PATH_SEGMENT_SPEED : prevSeg[insertAt - 1] ?? DEFAULT_PATH_SEGMENT_SPEED;
    const next = [...prevSeg];
    if (insertAt === 0) {
      return [DEFAULT_PATH_SEGMENT_SPEED, ...prevSeg].map(clampSegmentSpeed);
    }
    next.splice(insertAt - 1, 1, splitSpeed, splitSpeed);
    return next.map(clampSegmentSpeed);
  }

  if (newN === oldN - 1) {
    let removeAt = -1;
    for (let i = 0; i < oldN; i++) {
      if (!newIds.includes(oldIds[i])) {
        removeAt = i;
        break;
      }
    }
    if (removeAt < 0) return syncSegmentSpeedsLen(prevSeg, newN);
    const p = [...prevSeg];
    if (removeAt === 0) {
      p.shift();
    } else if (removeAt === oldN - 1) {
      p.pop();
    } else {
      const left = p[removeAt - 1] ?? DEFAULT_PATH_SEGMENT_SPEED;
      const right = p[removeAt] ?? DEFAULT_PATH_SEGMENT_SPEED;
      p.splice(removeAt - 1, 2, (left + right) / 2);
    }
    return p.map(clampSegmentSpeed);
  }

  return Array.from({ length: newN - 1 }, () => DEFAULT_PATH_SEGMENT_SPEED);
}
